<template>
  <div
    class="flex relative rounded-2xl focus:border-primary border-b-[#f0f1ff] transition-all duration-300 w-full"
    :class="{ '!border-primary border-b-primary': isFocus }"
  >
    <div class="relative w-1/2">
      <span class="absolute top-1/2 -translate-y-1/2 left-3 text-txsecondary text-sm">
        {{ fromLabel }}
      </span>
      <input
        v-model="fromValue"
        @input="handleFromInput"
        @keydown="handleKeydown"
        @paste="handlePaste"
        @focus="isFocus = true"
        @blur="isFocus = false"
        type="text"
        :placeholder="fromPlaceholder"
        :style="{ paddingLeft: fromPadding || '2.2rem' }"
        class="w-full px-3 py-3.5 rounded-l-[15px] text-sm border border-b-2 border-r-0 border-white placeholder:text-[#959B9F] flex items-center leading-3"
      />
    </div>
    <div class="relative w-1/2">
      <span class="absolute top-1/2 -translate-y-1/2 left-3 text-txsecondary text-sm">
        {{ toLabel }}
      </span>
      <input
        v-model="toValue"
        @input="handleToInput"
        @keydown="handleKeydown"
        @paste="handlePaste"
        @focus="isFocus = true"
        @blur="isFocus = false"
        type="text"
        :placeholder="toPlaceholder"
        :style="{ paddingLeft: toPadding || '2.2rem' }"
        class="w-full px-3 py-3.5 rounded-r-[15px] text-sm border border-b-2 border-white placeholder:text-[#959B9F] flex items-center leading-3"
      />
    </div>
    <el-slider
      v-model="rangeValue"
      :range="isRange"
      class="absolute -bottom-[1px] px-3 runway-none"
      tooltip-class="ui-tooltip"
      :min="min"
      :max="max"
      @update:model-value="handleRangeChange"
    />
  </div>
</template>

<script setup lang="ts">
  interface Props {
    min?: number;
    max?: number;
    fromLabel?: string;
    toLabel?: string;
    fromPlaceholder?: string;
    toPlaceholder?: string;
    fromPadding?: string;
    toPadding?: string;
    isRange?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    min: 0,
    max: 100,
    fromLabel: "От",
    toLabel: "До",
    fromPlaceholder: "0",
    toPlaceholder: "0",
    isRange: true,
  });

  const emit = defineEmits(["update:modelValue"]);

  const modelValue = defineModel<number[]>({ required: true });

  const rangeValue = ref<number[]>(modelValue.value);
  const fromValue = ref<number>(modelValue.value[0]);
  const toValue = ref<number>(modelValue.value[1]);

  const isFocus = ref(false);

  const handleFromInput = (e: Event) => {
    const target = e.target as HTMLInputElement;
    const value = target.value.replace(/\D/g, "");
    const numValue = value ? parseInt(value) : 0;
    fromValue.value = Math.min(Math.max(numValue, props.min), toValue.value);
    emit("update:modelValue", [fromValue.value, toValue.value]);
  };

  const handleToInput = (e: Event) => {
    const target = e.target as HTMLInputElement;
    const value = target.value.replace(/\D/g, "");
    const numValue = value ? parseInt(value) : 0;
    toValue.value = Math.min(Math.max(numValue, fromValue.value), props.max);
    emit("update:modelValue", [fromValue.value, toValue.value]);
  };

  const handleRangeChange = (value: number | number[]) => {
    if (Array.isArray(value)) {
      fromValue.value = value[0];
      toValue.value = value[1];
      emit("update:modelValue", value);
    }
  };

  const handleKeydown = (e: KeyboardEvent) => {
    // Разрешаем: цифры, Backspace, Delete, Tab, Escape, Enter, Home, End, стрелки
    const allowedKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "Escape",
      "Enter",
      "Home",
      "End",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
    ];

    // Разрешаем Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
    if (e.ctrlKey || e.metaKey) {
      const allowedCtrlKeys = ["a", "c", "v", "x", "z"];
      if (allowedCtrlKeys.includes(e.key.toLowerCase())) {
        return;
      }
    }

    // Разрешаем цифры
    if (/^[0-9]$/.test(e.key)) {
      return;
    }

    // Разрешаем специальные клавиши
    if (allowedKeys.includes(e.key)) {
      return;
    }

    // Блокируем все остальные клавиши
    e.preventDefault();
  };

  const handlePaste = (e: ClipboardEvent) => {
    e.preventDefault();
    const paste = e.clipboardData?.getData("text") || "";
    const numbersOnly = paste.replace(/\D/g, "");

    if (numbersOnly) {
      const target = e.target as HTMLInputElement;
      const currentValue = target.value;
      const selectionStart = target.selectionStart || 0;
      const selectionEnd = target.selectionEnd || 0;

      const newValue =
        currentValue.substring(0, selectionStart) +
        numbersOnly +
        currentValue.substring(selectionEnd);

      target.value = newValue;
      target.dispatchEvent(new Event("input", { bubbles: true }));
    }
  };

  watch(
    () => modelValue.value,
    (newValue) => {
      rangeValue.value = newValue;
      fromValue.value = newValue[0];
      toValue.value = newValue[1];
    }
  );
</script>

<style lang="scss" scoped>
  input {
    color: theme("colors.txmain");
    font-size: 14px;
    height: auto;
    background-color: theme("colors.bgmain");
    border: 0 !important;

    &:focus {
      --tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width)
        var(--tw-ring-offset-color);
      --tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(0px + var(--tw-ring-offset-width))
        var(--tw-ring-color);
      box-shadow: var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow, 0 0 #0000);
      --tw-ring-color: theme("colors.brfocus");
    }

    &::placeholder {
      color: theme("colors.txopacity");
    }
  }

  :deep(.el-slider) {
    .el-slider__runway {
      height: 2px;
      background-color: #f0f1ff;
    }

    .el-slider__bar {
      background-color: theme("colors.primary");
    }

    .el-slider__button {
      width: 14px;
      height: 14px;
      border: 2px solid white;
      background-color: theme("colors.primary");
      filter: drop-shadow(0px 1px 2.3px rgba(0, 0, 0, 0.12));
    }
  }
</style>

<style lang="scss">
  .ui-tooltip {
    background-color: white !important;
    color: theme("colors.txmain") !important;
    border: 0 !important;
    border-radius: 6px !important;
    box-shadow: 1px 2px 3px rgba(0, 0, 0, 0.12) !important;

    .el-popper__arrow {
      display: none;
    }
  }
</style>
